import { useState, useEffect } from 'react';
import isMobile from 'ismobilejs';

export interface MobileInfo {
  isMobile: boolean;
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  userAgent: string;
}

/**
 * Hook to detect mobile device type
 * Returns device information that can be used for conditional rendering
 *
 * Detection priority:
 * 1. User-Agent string (ismobilejs)
 * 2. Viewport width as fallback (for testing and responsive behavior)
 */
export const useMobile = (): MobileInfo => {
  const [deviceInfo, setDeviceInfo] = useState<MobileInfo>(() => {
    const device = isMobile(window.navigator);
    const viewportWidth = window.innerWidth;
    // Use viewport width as fallback for testing (<= 768px = mobile)
    const isMobileByViewport = viewportWidth <= 768;
    const isMobileDevice = device.any || isMobileByViewport;
    return {
      isMobile: isMobileDevice,
      isPhone: device.phone || isMobileByViewport,
      isTablet: device.tablet,
      isDesktop: !isMobileDevice,
      userAgent: window.navigator.userAgent,
    };
  });

  useEffect(() => {
    // Update device info on resize or viewport change
    const handleResize = () => {
      const device = isMobile(window.navigator);
      const viewportWidth = window.innerWidth;
      const isMobileByViewport = viewportWidth <= 768;
      const isMobileDevice = device.any || isMobileByViewport;

      setDeviceInfo({
        isMobile: isMobileDevice,
        isPhone: device.phone || isMobileByViewport,
        isTablet: device.tablet,
        isDesktop: !isMobileDevice,
        userAgent: window.navigator.userAgent,
      });
    };

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return deviceInfo;
};
